import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import {
  DESIGN_CARD_STATUSES,
  type DesignBoardOwner,
  type DesignCard,
  type DesignCardStatus,
  type OrderWithItems,
} from '@tailoring-crm/shared';
import { DesignCardDialog } from './design-card-dialog.js';
import { DesignSketchDialog } from './design-sketch-dialog.js';
import { DesignCardTile } from './design-card-tile.js';
import { EmptyState } from '../feedback/empty-state.js';
import { Button, useConfirm, ListSkeleton, useToast, cn } from '@tailoring-crm/ui';
import { useDeleteDesignCard, useDesignCards } from '../../lib/design-board/use-design-board.js';

/**
 * One board, wherever it hangs — an order's or a customer's.
 *
 * Extracted from the Design Board page when a card stopped being an order's alone. The three
 * screens differ in the OWNER they pass and in nothing else, which is the point: a shop that has
 * learnt to read the board on an order reads the same board on the person, filter chips and all.
 *
 * `readOnly` is for the boards that are a record rather than a working surface — any board being
 * shown beside somebody else's work, or to a role that may only look.
 */
export interface DesignBoardPanelProps {
  owner: DesignBoardOwner;
  /** The order behind an ORDER board, so a tile can name the garment a card is about. */
  order?: OrderWithItems | null;
  readOnly?: boolean;
  className?: string;
}

type FilterKey = 'all' | DesignCardStatus;

export function DesignBoardPanel({
  owner,
  order = null,
  readOnly = false,
  className,
}: DesignBoardPanelProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const confirm = useConfirm();

  const [filter, setFilter] = useState<FilterKey>('all');
  const [editing, setEditing] = useState<{
    mode: 'create' | 'edit';
    card: DesignCard | null;
  } | null>(null);
  /** The card whose canvas is open, if any. Only ever a card that already exists — a drawing is
      made ON a design, so there is nothing to draw on until one has been saved. */
  const [drawing, setDrawing] = useState<DesignCard | null>(null);

  const cards = useDesignCards(owner, filter === 'all' ? {} : { status: filter });
  const deleteCard = useDeleteDesignCard();
  const items = cards.data?.items ?? [];

  const askDelete = async (card: DesignCard): Promise<void> => {
    const confirmed = await confirm({
      title: t('designBoard.delete.title', { name: card.title }),
      description: t('designBoard.delete.description'),
      confirmLabel: t('designBoard.delete.confirm'),
      destructive: true,
    });
    if (!confirmed) return;
    await deleteCard.mutateAsync(card.id);
    toast({ title: t('designBoard.delete.done') });
  };

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {readOnly ? null : (
        <div>
          <Button onClick={() => setEditing({ mode: 'create', card: null })}>
            <Plus aria-hidden="true" />
            {t('designBoard.newDesign')}
          </Button>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {(['all', ...DESIGN_CARD_STATUSES] as const).map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={filter === key}
            onClick={() => setFilter(key)}
            className={cn(
              'rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              filter === key
                ? 'border-transparent bg-primary text-primary-foreground'
                : 'border-border hover:bg-accent',
            )}
          >
            {key === 'all' ? t('designBoard.filter.all') : t(`designBoard.status.${key}`)}
          </button>
        ))}
      </div>

      {cards.isPending ? <ListSkeleton rows={3} /> : null}

      {!cards.isPending && items.length === 0 ? (
        <EmptyState
          title={t('designBoard.emptyTitle')}
          description={
            readOnly ? t('designBoard.emptyReadOnly') : t('designBoard.emptyDescription')
          }
        />
      ) : null}

      {items.length > 0 ? (
        <ul
          className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="design-card-grid"
        >
          {items.map((card) => (
            <DesignCardTile
              key={card.id}
              card={card}
              order={order}
              {...(readOnly
                ? {}
                : {
                    onEdit: () => setEditing({ mode: 'edit', card }),
                    onDelete: () => void askDelete(card),
                  })}
              /* Offered on a read-only board only when there is something to look at. On a
                 working board it is always offered, because that is how a card gets its first
                 drawing. */
              {...(readOnly && !card.hasSketch ? {} : { onSketch: () => setDrawing(card) })}
            />
          ))}
        </ul>
      ) : null}

      {drawing ? (
        <DesignSketchDialog card={drawing} readOnly={readOnly} onClose={() => setDrawing(null)} />
      ) : null}

      {editing ? (
        <DesignCardDialog
          mode={editing.mode}
          owner={owner}
          card={editing.card}
          orderItems={order?.items ?? []}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
